import React, { useState, forwardRef } from 'react';
import { NavLink } from 'react-router-dom';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import makeStyles from '@mui/styles/makeStyles';
import { ListItem, Button, Collapse } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import { divideBy } from '../../../styles/styleUtils';

const RouterLink = (props, ref) => (
    <div ref={ref} style={{ flexGrow: 1 }}>
        {/* eslint-disable-next-line */}
        <NavLink {...props} />
    </div>
);

const CustomRouterLink = forwardRef(RouterLink);

const sidebarMenuListItem = makeStyles((theme) => {
    const appSidebarIcon = {
        display: 'flex',
        alignItems: 'center',
        marginRight: '16px'
    };
    return {
        appSidebarItem: {
            display: 'block',
            padding: `2px ${theme.themeVars.spacer} 1px 0`
        },
        appSidebarButton: {
            paddingTop: divideBy(theme.themeVars.spacer, 2.4),
            paddingBottom: divideBy(theme.themeVars.spacer, 2.4),
            paddingRight: divideBy(theme.themeVars.spacer, 2),
            justifyContent: 'flex-start',
            textTransform: 'none',
            borderRadius: '0 22px 22px 0',
            fontSize: '15px',
            width: '100%',
            fontWeight: 'normal',
            transition: theme.themeVars.transitionBase,
            color: theme.palette.secondary.main,
            whiteSpace: 'nowrap',
            '&.active, &:hover': {
                color: theme.palette.primary.main,
                background: theme.palette.grey[100]
            }
        },
        appSidebarIconSecondary: {
            ...appSidebarIcon,
            color: theme.palette.secondary.main
        },
        appSidebarIconPrimary: {
            ...appSidebarIcon,
            color: theme.palette.primary.main
        },
        sidebarExpandIcon: {
            marginLeft: 'auto',
            height: '16px',
            width: '16px',
            opacity: '.6',
            transition: theme.themeVars.transitionBase
        },
        sidebarExpandIconRotate: {
            opacity: '.9',
            transform: 'rotate(180deg)'
        },

        menuItemLabel: {
            display: 'flex',
            alignItems: 'center',
            marginLeft: 'auto'
        },
        activeItem: {
            color: theme.palette.primary.main,
            fontWeight: '500'
        },
        appSidebarButtonWrapper: {
            padding: '4px 17px',
            justifyContent: 'flex-start',
            textTransform: 'none',
            width: '100%',
            borderRadius: '3px',
            transition: theme.themeVars.transitionBase,
            fontWeight: 'normal',
            fontSize: '14px',
            color: theme.palette.secondary.main,
            '&.depth-0': {
                fontWeight: '500'
            },
            '&.active, &:hover': {
                background: 'none',
                color: theme.palette.primary.main
            }
        }
    };
});

const SidebarMenuListItem = (props) => {
    const {
        title,
        href,
        depth,
        children,
        icon: Icon,
        className,
        open: openProp,
        label: Label,
        ...rest
    } = props;

    const classes = sidebarMenuListItem();

    const [open, setOpen] = useState(openProp);

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    let paddingLeft = 14;

    // if (depth > 0) {
    //     paddingLeft = 32 + 20 * depth;
    // }

    const style = {
        paddingLeft
    };

    return (
        // eslint-disable-next-line
        <ListItem {...rest} className={clsx(classes.appSidebarItem, className)} disableGutters>
            {children ? (
                <>
                    <Button
                        color="primary"
                        className={clsx(classes.appSidebarButton, { active: open })}
                        onClick={handleToggle}
                        style={style}
                    >
                        {Icon && <Icon className={classes.appSidebarIconSecondary} />}
                        <span>{title}</span>
                        {open ? (
                            <ExpandLessIcon className={classes.sidebarExpandIcon} color="inherit" />
                        ) : (
                            <ExpandLessIcon
                                className={`${classes.sidebarExpandIcon} ${classes.sidebarExpandIconRotate}`}
                                color="inherit"
                            />
                        )}
                    </Button>
                    <Collapse in={open}>{children}</Collapse>
                </>
            ) : (
                <Button
                    activeClassName={classes.activeItem}
                    color="primary"
                    disableRipple
                    variant="text"
                    className={clsx(classes.appSidebarButtonWrapper, `depth-${depth}`)}
                    component={CustomRouterLink}
                    exact
                    style={style}
                    to={href}
                >
                    {Icon && <Icon className={classes.appSidebarIconPrimary} />}
                    {title}
                    {Label && (
                        <span className={classes.menuItemLabel}>
                            <Label />
                        </span>
                    )}
                </Button>
            )}
        </ListItem>
    );
};

SidebarMenuListItem.defaultProps = {
    children: undefined,
    depth: 0,
    open: false,
    className: '',
    href: '',
    label: '',
    // eslint-disable-next-line react/display-name
    icon: () => <></>
};
SidebarMenuListItem.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    depth: PropTypes.number,
    href: PropTypes.string,
    icon: PropTypes.elementType,
    label: PropTypes.string,
    open: PropTypes.bool,
    title: PropTypes.string.isRequired
};

export default SidebarMenuListItem;
